import React from "react";
import { ScoreRing } from "./ScoreRing";
import { EscalationBanner } from "./EscalationBanner";
import { REPORT_COPY, SOURCE_LINKS } from "../../lib/content";
import { ExternalLink, Utensils, ClipboardList, BookOpen, ShieldAlert } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Full report screen — score hero, emergency banner, category grid, diet,
 * recommendations, sources, disclaimer, CTA.
 */
export function StepReport({ report, cat, onDone }) {
    if (!report) return null;
    const date = new Date().toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
    });

    const showEscalation =
        report.vet_visit ||
        (Array.isArray(report.emergency_alerts) && report.emergency_alerts.length > 0);

    return (
        <div className="pb-32" data-testid="step-report">
            {/* Score hero */}
            <div
                className="mt-2 rounded-3xl bg-white p-6 shadow-[0_8px_30px_rgba(42,42,42,0.05)]"
                data-testid="report-score-hero"
            >
                <div className="flex items-start justify-between">
                    <div>
                        <span className="eyebrow text-meow-teal" data-testid="report-badge">
                            {REPORT_COPY.scoreBadge}
                        </span>
                        <div className="mt-1 font-display text-lg text-meow-charcoal">
                            {cat.name} · {date}
                        </div>
                    </div>
                </div>
                <div className="mt-5 flex flex-col items-center">
                    <ScoreRing score={report.health_score} size={200} stroke={14} />
                    <div className="mt-3 eyebrow text-meow-muted">
                        {REPORT_COPY.scoreLabel}
                    </div>
                </div>
                <p
                    className="mt-5 text-[14px] leading-relaxed text-meow-charcoal/90"
                    data-testid="report-summary"
                >
                    {report.summary}
                </p>
            </div>

            {/* Escalation banner */}
            {showEscalation && (
                <div className="mt-5" data-testid="report-escalation-wrap">
                    <EscalationBanner
                        title={REPORT_COPY.sectionHeadings.emergency}
                        alerts={report.emergency_alerts || []}
                        footer={REPORT_COPY.emergencyFooter}
                        testId="report-emergency-banner"
                    />
                </div>
            )}

            {/* Critical findings */}
            {Array.isArray(report.critical_findings) &&
                report.critical_findings.length > 0 && (
                    <Section
                        icon={<ShieldAlert className="h-4 w-4" />}
                        title={REPORT_COPY.sectionHeadings.critical}
                        testId="report-critical"
                    >
                        <ul className="space-y-2">
                            {report.critical_findings.map((f, i) => (
                                <li
                                    key={i}
                                    className="flex gap-2.5 text-[14px] leading-relaxed text-meow-charcoal/90"
                                >
                                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-meow-gold" />
                                    <span>{f}</span>
                                </li>
                            ))}
                        </ul>
                    </Section>
                )}

            {/* 8 categories */}
            <Section
                icon={<ClipboardList className="h-4 w-4" />}
                title={REPORT_COPY.sectionHeadings.categories}
                testId="report-categories"
            >
                <div className="space-y-3">
                    {(report.categories || []).map((c, idx) => (
                        <CategoryRow key={c.name + idx} category={c} idx={idx} />
                    ))}
                </div>
            </Section>

            {/* Diet plan */}
            {report.diet_plan && (
                <Section
                    icon={<Utensils className="h-4 w-4" />}
                    title={REPORT_COPY.sectionHeadings.diet}
                    testId="report-diet"
                >
                    <p className="text-[14px] leading-relaxed text-meow-charcoal/90">
                        {report.diet_plan.summary}
                    </p>
                    <div className="mt-4 space-y-3">
                        {(report.diet_plan.meals || []).map((m, i) => (
                            <div
                                key={i}
                                className="rounded-2xl bg-white p-4 shadow-[0_4px_20px_rgba(42,42,42,0.04)]"
                                data-testid={`report-diet-meal-${i}`}
                            >
                                <div className="font-display text-[15px] font-medium text-meow-charcoal">
                                    {m.meal}
                                </div>
                                <ul className="mt-2 space-y-1 text-[13.5px] leading-relaxed text-meow-charcoal/85">
                                    {(m.items || []).map((it, j) => (
                                        <li key={j} className="flex gap-2">
                                            <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-meow-teal" />
                                            <span>{it}</span>
                                        </li>
                                    ))}
                                </ul>
                                {m.notes && (
                                    <p className="mt-2 text-[12.5px] italic text-meow-muted">
                                        {m.notes}
                                    </p>
                                )}
                            </div>
                        ))}
                    </div>
                    {Array.isArray(report.diet_plan.avoid) &&
                        report.diet_plan.avoid.length > 0 && (
                            <div className="mt-4 rounded-xl bg-meow-emergency px-4 py-3">
                                <div className="eyebrow text-meow-red">Avoid</div>
                                <ul className="mt-2 space-y-1 text-[13.5px] leading-relaxed text-meow-charcoal/85">
                                    {report.diet_plan.avoid.map((a, i) => (
                                        <li key={i} className="flex gap-2">
                                            <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-meow-red" />
                                            <span>{a}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                </Section>
            )}

            {/* Recommendations */}
            {Array.isArray(report.recommendations) &&
                report.recommendations.length > 0 && (
                    <Section
                        icon={<ClipboardList className="h-4 w-4" />}
                        title={REPORT_COPY.sectionHeadings.recommendations}
                        testId="report-recommendations"
                    >
                        <ul className="space-y-2">
                            {report.recommendations.map((r, i) => (
                                <li
                                    key={i}
                                    className="flex gap-2.5 text-[14px] leading-relaxed text-meow-charcoal/90"
                                >
                                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-meow-teal" />
                                    <span>{r}</span>
                                </li>
                            ))}
                        </ul>
                    </Section>
                )}

            {/* Vet references */}
            {Array.isArray(report.vet_references) &&
                report.vet_references.length > 0 && (
                    <Section
                        icon={<BookOpen className="h-4 w-4" />}
                        title={REPORT_COPY.sectionHeadings.references}
                        testId="report-references"
                    >
                        <ul className="flex flex-wrap gap-1.5">
                            {report.vet_references.map((r, i) => (
                                <li
                                    key={i}
                                    className="rounded-full bg-meow-icon px-3 py-1 text-[12px] font-medium text-meow-teal"
                                >
                                    {r}
                                </li>
                            ))}
                        </ul>
                    </Section>
                )}

            {/* Sources */}
            <Section
                icon={<BookOpen className="h-4 w-4" />}
                title={REPORT_COPY.sectionHeadings.sources}
                testId="report-sources"
            >
                <ul className="space-y-2">
                    {SOURCE_LINKS.map((s) => (
                        <li key={s.url}>
                            <a
                                href={s.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 text-[14px] font-medium text-meow-teal hover:underline"
                            >
                                {s.label}
                                <ExternalLink className="h-3.5 w-3.5" strokeWidth={2} />
                            </a>
                        </li>
                    ))}
                </ul>
            </Section>

            {/* Short disclaimer */}
            <div
                className="mt-6 rounded-2xl bg-white/60 p-4 text-[12.5px] leading-relaxed text-meow-muted"
                data-testid="report-disclaimer"
            >
                {REPORT_COPY.disclaimerShort}{" "}
                <Link
                    to="/disclaimer"
                    className="font-medium text-meow-teal hover:underline"
                    data-testid="report-disclaimer-link"
                >
                    {REPORT_COPY.disclaimerReadFull}
                </Link>
            </div>

            {/* Sticky CTA */}
            <div className="pointer-events-none fixed inset-x-0 bottom-0 z-30">
                <div className="mx-auto max-w-[440px] bg-gradient-to-t from-meow-cream via-meow-cream/95 to-transparent px-5 pb-5 pt-8">
                    <div className="pointer-events-auto">
                        <button
                            type="button"
                            className="meow-btn-primary w-full"
                            onClick={onDone}
                            data-testid="report-cta-continue"
                        >
                            {REPORT_COPY.ctaContinue}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Section({ icon, title, children, testId }) {
    return (
        <section className="mt-6" data-testid={testId}>
            <div className="mb-3 flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-meow-icon text-meow-teal">
                    {icon}
                </div>
                <h2 className="font-display text-[1.15rem] font-medium text-meow-charcoal">
                    {title}
                </h2>
            </div>
            {children}
        </section>
    );
}

function CategoryRow({ category, idx }) {
    const status = category.status || "good";
    const chipClass =
        status === "concern"
            ? "chip chip-concern"
            : status === "monitor"
            ? "chip chip-monitor"
            : "chip chip-good";
    const scoreColor =
        category.score >= 80
            ? "text-meow-teal"
            : category.score >= 60
            ? "text-meow-gold"
            : "text-meow-red";
    return (
        <div
            className="rounded-2xl bg-white p-4 shadow-[0_4px_20px_rgba(42,42,42,0.04)]"
            data-testid={`report-category-${idx}`}
        >
            <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                    <div className="font-display text-[15.5px] font-medium text-meow-charcoal">
                        {category.name}
                    </div>
                    <p className="mt-1 text-[13.5px] leading-relaxed text-meow-muted">
                        {category.findings}
                    </p>
                </div>
                <div className="flex flex-col items-end gap-1.5">
                    <span
                        className={`font-display text-2xl font-medium tabular-nums leading-none ${scoreColor}`}
                        data-testid={`report-category-score-${idx}`}
                    >
                        {category.score}
                    </span>
                    <span className={chipClass} data-testid={`report-category-status-${idx}`}>
                        {status === "good"
                            ? "Good"
                            : status === "monitor"
                            ? "Monitor"
                            : "Concern"}
                    </span>
                </div>
            </div>
        </div>
    );
}
